import { useState } from "react";
import { CheckCircle2, MessageSquareHeart, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DonationReport, MugHint, BankDetails } from "@/components/donate/BankDonate";
import { Reveal } from "./Bits";
import { TRUST } from "./content";
import { AmountPicker, parseAmount, euro } from "./AmountPicker";

function NameHint() {
  const { user } = useAuth();
  if (user?.supporter) return <p className="mt-5 flex gap-2 rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-4 text-sm font-semibold text-emerald-800" data-testid="cs-me-supporter"><CheckCircle2 className="w-5 h-5 shrink-0" /> Du bist schon Projekt Unterstützer – danke, dass du Teil unserer Geschichte bist! ❤️</p>;
  return (
    <p className="mt-5 flex gap-2.5 rounded-2xl bg-rose-50 ring-1 ring-pink-200 p-4 text-sm text-slate-700 leading-relaxed" data-testid="cs-name-hint">
      <MessageSquareHeart className="w-5 h-5 shrink-0 text-rose-500" />
      <span>Gib bei der Überweisung deinen <b>persönlichen Code</b> als Verwendungszweck an – dann können wir dich eindeutig zuordnen und als <b>Projekt Unterstützer</b> freischalten.</span>
    </p>
  );
}

export function SupportBox() {
  const [sel, setSel] = useState(5);
  const [custom, setCustom] = useState("");
  const [touched, setTouched] = useState(false);
  const amount = sel === "custom" ? parseAmount(custom) : sel;
  const error = sel === "custom" && (touched || custom) && !amount ? "Bitte gib einen gültigen Betrag ein (z. B. 7,50 – höchstens 10.000 €)." : "";
  return (
    <section id="bank-support" className="scroll-mt-40 lg:scroll-mt-28" data-testid="cs-bank-support" aria-labelledby="cs-bank-title">
      <Reveal className="relative overflow-hidden rounded-[2rem] bg-white border border-pink-100 shadow-[0_30px_70px_-35px_rgba(225,29,99,.6)]">
        <div className="h-1.5 pink-gradient" aria-hidden />
        <div className="grid lg:grid-cols-[1fr_1.05fr]">
          <div className="p-6 sm:p-10 bg-gradient-to-br from-rose-50 via-white to-pink-50">
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-rose-500">Freiwillige Unterstützung</p>
            <h2 id="cs-bank-title" className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900 mt-1.5 leading-tight">❤️ Möchtest du uns unterstützen?</h2>
            <div className="mt-4 space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed">
              <p>Wenn dir unsere Community gefällt und du an unsere Idee glaubst, freuen wir uns über jede freiwillige Unterstützung.</p>
              <p>Ganz egal, ob 1 €, 5 €, 10 € oder ein anderer Betrag. Jeder Beitrag hilft uns dabei, unsere Plattform weiterzuentwickeln und langfristig zu betreiben.</p>
              <p className="font-semibold text-slate-800">Und wenn du uns nicht finanziell unterstützen kannst oder möchtest, ist das vollkommen in Ordnung. ❤️</p>
            </div>
            <ul className="mt-6 space-y-2.5" data-testid="cs-trust">
              {TRUST.map(([e, t]) => <li key={t} className="flex gap-2.5 text-sm text-slate-600"><span className="shrink-0 w-6 text-center" aria-hidden>{e}</span>{t}</li>)}
            </ul>
          </div>
          <div className="p-6 sm:p-10">
            <AmountPicker sel={sel} setSel={(v) => { setSel(v); setTouched(false); }} custom={custom} setCustom={setCustom} error={error} />
            <div className="mt-3 mb-1"><MugHint value={amount || 0} testId="cs-mug-hint" /></div>
            <BankDetails testId="cs-bank" amount={amount || 0} />
            <NameHint />
            <p className="mt-3 flex gap-2 text-xs sm:text-sm text-slate-600 leading-relaxed" data-testid="cs-vip-rule"><Heart className="w-4 h-4 shrink-0 mt-0.5 text-rose-500" fill="currentColor" /> Ab 25 € schalten wir dich als Dankeschön 7 Tage als Projekt Unterstützer frei.</p>
            <div className="mt-2 text-center"><DonationReport value={amount} testId="cs-donation-report" /></div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
